/**
 * Echo Hunt -- the LLM call helper (P3).
 *
 * Every Echo Hunt pipeline step calls the LLM through `CallLLM`. It:
 *  - collects the streamed text from a pluggable `LLMBackend`;
 *  - when `json` is set, extracts and parses the first JSON value, validates it against the small
 *    "JSONSchema-ish" spec, and retries (up to 2x) on parse/validation failure, sending the error
 *    back to the model;
 *  - returns `{text, parsed, model, outputTokens, ms}`;
 *  - tallies calls/tokens on a per-round `LLMCallCounter`.
 *
 * Feature code must NEVER call `GenerateNextMessage_OpenRouter`/`_Anthropic` (or
 * `GenerateNextMessage_Generic`) directly -- it calls this, so that tests can swap in a mock backend
 * and so that per-role model settings (§16.1) are honored in one place.
 *
 * PURE module: no React, no `store`, no DB imports (see README.md). The real (network-backed) backend
 * lives in `LLM_Live.ts`.
 *
 * See §16.0 of `Docs/Plans/Others/2026-10-19_EchoHunt.md`.
 */
import {LLMRole, NormalizeModelOverride, ReasoningEffort, RoleModelMap} from "./Roles.js";

// backend interface
// ==========

/** One request to an LLM backend. `user` is the *complete* final user message (the retry path below
 * folds the previous rejected reply + its error straight into this text, so a backend never has to
 * understand the retry protocol). */
export type LLMRequest = {
	role: LLMRole;
	/** the resolved model id for this role; "" means "let the backend/settings decide" */
	model: string;
	system?: string|n;
	user: string;
	temperature?: number|n;
	/** cap on generated tokens, where the backend supports it */
	maxOutputTokens?: number|n;
	/** reasoning effort to request (2026-10-21); null = leave the helper's default (disabled) alone. Set only
	 * when the model needs it -- see `ModelCapabilities.ResolveReasoningEffort`, which the backend applies. */
	reasoningEffort?: ReasoningEffort|n;
	/** 0-based attempt index (0 = first call, 1.. = retries after a bad reply) */
	attempt: number;
};

export type LLMResponse = {
	text: string;
	/** tokens reported by the provider for this call, when available */
	outputTokens?: number|n;
	model: string;
};

/** A pluggable LLM transport. Later phases accept one as a parameter so tests never hit the network. */
export type LLMBackend = {
	call(request: LLMRequest): Promise<LLMResponse>;
};

// per-round call/token counter (§13.1 "token cost of this call, for the per-round counter")
// ==========

export class LLMCallCounter {
	readonly byRole: Record<string, {calls: number, outputTokens: number}> = {};

	get callCount(): number {
		return Object.values(this.byRole).reduce((sum, a)=>sum + a.calls, 0);
	}
	get outputTokens(): number {
		return Object.values(this.byRole).reduce((sum, a)=>sum + a.outputTokens, 0);
	}

	record(role: LLMRole, outputTokens: number|n) {
		const entry = this.byRole[role] ??= {calls: 0, outputTokens: 0};
		entry.calls++;
		entry.outputTokens += outputTokens ?? 0;
	}

	/** Plain-object snapshot, for attaching to a round log. */
	toJSON(): {calls: number, outputTokens: number, byRole: Record<string, {calls: number, outputTokens: number}>} {
		return {
			calls: this.callCount,
			outputTokens: this.outputTokens,
			byRole: JSON.parse(JSON.stringify(this.byRole)),
		};
	}
}

// model-role resolution (§16.1)
// ==========

/** Which model a role should use: its override if set, else the global model. */
export function ResolveModelForRole(modelRoles: RoleModelMap|n, role: LLMRole, globalModel: string): string {
	return NormalizeModelOverride(modelRoles?.[role]) ?? globalModel;
}

// JSON extraction
// ==========

/** Removes ``` fences (```json ... ``` or ``` ... ```) so a fenced reply parses. */
export function StripCodeFences(text: string): string {
	return text.replace(/```[A-Za-z0-9_-]*\s*\n?/g, "").replace(/```/g, "");
}

/** Finds the first balanced JSON object/array substring in `text`, or null if there is none.
 * Respects strings and escapes, so braces inside string literals don't confuse the depth count. */
export function FindFirstJSONSubstring(text: string): string|n {
	const starts = ["{", "["];
	let startIndex = -1;
	for (let i = 0; i < text.length; i++) {
		if (starts.includes(text[i])) {
			startIndex = i;
			break;
		}
	}
	if (startIndex == -1) return null;

	let depth = 0;
	let inString = false;
	let escaped = false;
	for (let i = startIndex; i < text.length; i++) {
		const char = text[i];
		if (inString) {
			if (escaped) {
				escaped = false;
			} else if (char == "\\") {
				escaped = true;
			} else if (char == "\"") {
				inString = false;
			}
			continue;
		}

		if (char == "\"") {
			inString = true;
		} else if (char == "{" || char == "[") {
			depth++;
		} else if (char == "}" || char == "]") {
			depth--;
			if (depth == 0) return text.substring(startIndex, i + 1);
		}
	}
	return null; // unbalanced (truncated reply)
}

export type JSONParseOutcome = {ok: true, value: unknown, raw: string, error?: undefined} | {ok: false, value?: undefined, raw: string|n, error: string};

/** Extracts + parses the first JSON value in a model reply. */
export function ExtractFirstJSON(text: string): JSONParseOutcome {
	const stripped = StripCodeFences(text);
	const raw = FindFirstJSONSubstring(stripped) ?? FindFirstJSONSubstring(text);
	if (raw == null) {
		return {ok: false, raw: null, error: `The reply did not contain a JSON object or array. Reply: ${PreviewText(text)}`};
	}
	try {
		return {ok: true, value: JSON.parse(raw), raw};
	} catch (ex) {
		return {ok: false, raw, error: `The reply's JSON could not be parsed (${(ex as Error).message}).`};
	}
}

// JSON validation (a deliberately small "JSONSchema-ish" subset)
// ==========

export type JSONSchemaLike = {
	type?: "object" | "array" | "string" | "number" | "integer" | "boolean" | "null";
	required?: string[];
	properties?: Record<string, JSONSchemaLike>;
	items?: JSONSchemaLike;
	enum?: unknown[];
	minItems?: number;
	maxItems?: number;
	minLength?: number;
	maxLength?: number;
};

/** Returns a list of human-readable problems (empty = valid). Used both to retry the model and, in
 * later phases, to validate the weaver's locked-ID output (§8.1). */
export function ValidateJSON(value: unknown, schema: JSONSchemaLike, path = ""): string[] {
	const problems: string[] = [];
	const at = path.length > 0 ? path : "(root)";

	if (schema.type != null && !MatchesType(value, schema.type)) {
		problems.push(`${at} should be of type "${schema.type}", got ${DescribeType(value)}.`);
		return problems; // further checks would just be noise
	}

	if (schema.enum != null && !schema.enum.some(a=>a == value)) {
		problems.push(`${at} should be one of ${schema.enum.map(a=>JSON.stringify(a)).join(", ")}; got ${JSON.stringify(value)}.`);
	}

	if (typeof value == "string") {
		if (schema.minLength != null && value.length < schema.minLength) problems.push(`${at} should be at least ${schema.minLength} characters.`);
		if (schema.maxLength != null && value.length > schema.maxLength) problems.push(`${at} should be at most ${schema.maxLength} characters.`);
	}

	if (Array.isArray(value)) {
		if (schema.minItems != null && value.length < schema.minItems) problems.push(`${at} should have at least ${schema.minItems} items (got ${value.length}).`);
		if (schema.maxItems != null && value.length > schema.maxItems) problems.push(`${at} should have at most ${schema.maxItems} items (got ${value.length}).`);
		if (schema.items != null) {
			value.forEach((item, i)=>problems.push(...ValidateJSON(item, schema.items!, `${at}[${i}]`)));
		}
	}

	if (value != null && typeof value == "object" && !Array.isArray(value)) {
		const obj = value as Record<string, unknown>;
		for (const key of schema.required ?? []) {
			if (!(key in obj)) problems.push(`${at} is missing the required field "${key}".`);
		}
		for (const [key, childSchema] of Object.entries(schema.properties ?? {})) {
			if (key in obj) problems.push(...ValidateJSON(obj[key], childSchema, path.length > 0 ? `${at}.${key}` : key));
		}
	}

	return problems;
}

function MatchesType(value: unknown, type: NonNullable<JSONSchemaLike["type"]>): boolean {
	switch (type) {
		case "null": return value === null;
		case "array": return Array.isArray(value);
		case "object": return value != null && typeof value == "object" && !Array.isArray(value);
		case "integer": return typeof value == "number" && Number.isInteger(value);
		case "number": return typeof value == "number" && Number.isFinite(value);
		case "string": return typeof value == "string";
		case "boolean": return typeof value == "boolean";
	}
}
function DescribeType(value: unknown): string {
	if (value === null) return "null";
	if (Array.isArray(value)) return "array";
	return typeof value;
}

// the call itself
// ==========

export type CallLLMOptions = {
	role: LLMRole;
	system?: string|n;
	user: string;
	/** when set, the reply must be JSON matching this (small) schema; parse/validation failures retry */
	json?: JSONSchemaLike|n;
	temperature?: number|n;
	/** resolved model id for this role (see `ResolveModelForRole`); omit to let the backend decide */
	model?: string|n;
	maxOutputTokens?: number|n;
	/** reasoning effort override (2026-10-21); omit for "auto", which is resolved per-model by the backend */
	reasoningEffort?: ReasoningEffort|n;
	/** total attempts, i.e. 1 initial call + (maxAttempts - 1) retries. Default 3 (§P3: "retries up to 2x") */
	maxAttempts?: number;
	/** per-round tally (§13.1); omitted for one-off/dev calls */
	counter?: LLMCallCounter|n;
};

export type CallLLMResult = {
	text: string;
	/** parsed JSON value when `json` was requested; null otherwise */
	parsed: unknown|n;
	model: string;
	outputTokens: number;
	ms: number;
	/** how many backend calls it took (1 = no retry) */
	attempts: number;
	/** the final validation/parse error, when all attempts failed (the parsed value is then null) */
	error?: string|n;
};

/** Calls the LLM for one pipeline step, with JSON extraction/validation and bounded retries. */
export async function CallLLM(backend: LLMBackend, options: CallLLMOptions): Promise<CallLLMResult> {
	const maxAttempts = Math.max(1, options.maxAttempts ?? 3);
	const startTime = Date.now();
	const model = options.model ?? "";

	let lastText = "";
	let lastError: string|n = null;
	let lastTokens = 0;

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		// on a retry, fold the previous rejected reply + the reason into the user message, so any backend
		// can serve the retry without understanding a multi-message retry protocol
		const user = attempt == 0 ? options.user : [
			options.user,
			"",
			"## Your previous reply (rejected)",
			lastText,
			"",
			"## Problem with that reply",
			lastError,
			"",
			options.json != null
				? "Reply again, with ONLY the corrected JSON value. No prose, no code fences, no explanation."
				: "Reply again, fixing the problem above.",
		].join("\n");

		const response = await backend.call({
			role: options.role,
			model,
			system: options.system,
			user,
			temperature: options.temperature,
			maxOutputTokens: options.maxOutputTokens,
			reasoningEffort: options.reasoningEffort,
			attempt,
		});
		const tokens = response.outputTokens ?? 0;
		lastText = response.text ?? "";
		lastTokens += tokens;
		options.counter?.record(options.role, tokens);

		if (options.json == null) {
			return {text: lastText, parsed: null, model: response.model || model, outputTokens: lastTokens, ms: Date.now() - startTime, attempts: attempt + 1};
		}

		// JSON path: extract -> parse -> validate; any failure becomes the retry reason
		const extracted = ExtractFirstJSON(lastText);
		if (!extracted.ok) {
			lastError = extracted.error;
			continue;
		}
		const problems = ValidateJSON(extracted.value, options.json);
		if (problems.length > 0) {
			lastError = `The JSON's shape is wrong:\n- ${problems.join("\n- ")}`;
			continue;
		}

		return {
			text: lastText,
			parsed: extracted.value,
			model: response.model || model,
			outputTokens: lastTokens,
			ms: Date.now() - startTime,
			attempts: attempt + 1,
		};
	}

	// all attempts failed -- return the last text (so callers/logs can see what the model actually said)
	return {
		text: lastText,
		parsed: null,
		model,
		outputTokens: lastTokens,
		ms: Date.now() - startTime,
		attempts: maxAttempts,
		error: lastError,
	};
}

// mock backend
// ==========

/** A canned reply for a role: a literal string, or a function of the request (so a test can vary the
 * reply per attempt, e.g. to exercise the JSON retry). */
export type MockReply = string | ((request: LLMRequest)=>string);

/** An in-memory backend for unit tests and the "no network" dev path. Records every request it saw. */
export class MockLLMBackend implements LLMBackend {
	constructor(responses: Partial<Record<LLMRole, MockReply>>, options?: {outputTokensPerCall?: number, model?: string}) {
		this.responses = responses;
		this.outputTokensPerCall = options?.outputTokensPerCall;
		this.model = options?.model ?? "mock-model";
	}

	readonly responses: Partial<Record<LLMRole, MockReply>>;
	readonly outputTokensPerCall: number|n;
	readonly model: string;
	/** every request this backend received, in order (assert on this to test retry prompts) */
	readonly requests: LLMRequest[] = [];

	async call(request: LLMRequest): Promise<LLMResponse> {
		this.requests.push(request);
		const reply = this.responses[request.role];
		const text = reply == null
			? `[mock reply for role "${request.role}", attempt ${request.attempt}]`
			: typeof reply == "function" ? reply(request) : reply;
		return {text, outputTokens: this.outputTokensPerCall ?? undefined, model: this.model};
	}

	/** Requests for one role, in order. */
	requestsFor(role: LLMRole): LLMRequest[] {
		return this.requests.filter(a=>a.role == role);
	}
}

// misc
// ==========

/** Truncates text for inclusion in an error message. */
export function PreviewText(text: string, maxLength = 300): string {
	const collapsed = text.replace(/\s+/g, " ").trim();
	return collapsed.length <= maxLength ? collapsed : `${collapsed.substring(0, maxLength)}…`;
}
